import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * Wraps window.fetch so that any same-origin JSON response containing a
 * `segmentDataLayer` array is replayed through window.analytics — porting
 * SFRA's client-side segmentInit.js consumer to MRT.
 *
 * SFRA controllers (e.g. EmailSubscribe-SubscribeEmail, the target of the
 * Bloomreach homepage email popup) build their Segment events server-side
 * and return them in the response body with `isSegmentEventSent: 0`. On
 * SFRA pages a client-side script picks them up; on MRT there was no
 * consumer, so the events sat in the response body and never reached
 * Segment. This restores that step. SFCC-10693.
 */
export const installSegmentDataLayerInterceptor = () => {
    if (typeof window === 'undefined' || window.__segmentDataLayerInterceptorInstalled) {
        return
    }

    const originalFetch = window.fetch.bind(window)
    const sameOrigin = window.location.origin

    window.fetch = async function (...args) {
        const response = await originalFetch(...args)
        try {
            const input = args[0]
            const requestUrl =
                typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url
            const isSameOrigin =
                requestUrl && (requestUrl.startsWith('/') || requestUrl.startsWith(sameOrigin))
            if (!isSameOrigin) return response

            const contentType = response.headers.get('content-type') || ''
            if (!contentType.includes('json')) return response

            const data = await response.clone().json()
            const segmentDataLayer = data?.segmentDataLayer
            if (!Array.isArray(segmentDataLayer) || segmentDataLayer.length === 0) return response

            const analytics = window.analytics
            if (!analytics) return response

            // Fire identify entries before any track entries so the
            // userId is set on the analytics instance before tracks ship —
            // otherwise consent tracks lose the userId and get dropped by
            // the source-side consent middleware. Mirrors SFRA
            // segmentInit.js's identify-first sort.
            const ordered = [...segmentDataLayer].sort((a, b) => {
                if (a?.action === 'identify' && b?.action !== 'identify') return -1
                if (a?.action !== 'identify' && b?.action === 'identify') return 1
                return 0
            })

            ordered.forEach((item) => {
                if (!item || item.isSegmentEventSent) return
                try {
                    if (item.action === 'identify') {
                        analytics.identify(item.event, item.data)
                    } else if (item.action === 'track') {
                        analytics.track(item.event, item.data)
                    }
                } catch (err) {
                    logger.error('Failed to dispatch segmentDataLayer item', {
                        namespace: 'segment.segmentDataLayerInterceptor',
                        additionalProperties: {
                            action: item.action,
                            event: item.event,
                            error: err
                        }
                    })
                }
            })
        } catch (e) {
            // Body not JSON, already consumed, or other transient parse issue.
            // Never block the response on interceptor failure.
        }
        return response
    }

    window.__segmentDataLayerInterceptorInstalled = true
}
